import { Box, Typography } from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import { hasNotificationNavigation, type ApiNotification } from "../../utils/notificationsApi";
import {
  BORDER_DEFAULT,
  BORDER_LIGHT,
  FONT_FAMILY,
  PRIMARY,
  PRIMARY_LIGHT,
  RADIUS_CARD,
  TEXT_MUTED,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from "./notificationTheme";

type NotificationMeta = {
  Icon: typeof NotificationsActiveOutlinedIcon;
  iconBg: string;
  iconColor: string;
  label: string;
};

const getNotificationMeta = (type: string): NotificationMeta => {
  switch (type) {
    case "APPLICATION_SHORTLISTED":
      return {
        Icon: CheckCircleOutlineIcon,
        iconBg: "rgba(46, 125, 50, 0.12)",
        iconColor: "#2E7D32",
        label: "Shortlisted",
      };
    case "APPLICATION_DECLINED":
      return {
        Icon: CancelOutlinedIcon,
        iconBg: "rgba(198, 40, 40, 0.1)",
        iconColor: "#C62828",
        label: "Declined",
      };
    case "JOB_RECOMMENDED":
      return {
        Icon: AutoAwesomeOutlinedIcon,
        iconBg: PRIMARY_LIGHT,
        iconColor: PRIMARY,
        label: "Recommended",
      };
    case "APPLICATION_UPDATE":
    default:
      if (type?.includes("JOB") || type?.includes("APPLICATION")) {
        return {
          Icon: WorkOutlineOutlinedIcon,
          iconBg: PRIMARY_LIGHT,
          iconColor: PRIMARY,
          label: type.replace(/_/g, " "),
        };
      }
      return {
        Icon: NotificationsActiveOutlinedIcon,
        iconBg: PRIMARY_LIGHT,
        iconColor: PRIMARY,
        label: type?.replace(/_/g, " ") || "Update",
      };
  }
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24 && d.toDateString() === now.toDateString()) return `${diffHours}h ago`;
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
};

type NotificationCardProps = {
  item: ApiNotification;
  onClick: () => void;
};

const NotificationCard = ({ item, onClick }: NotificationCardProps): JSX.Element => {
  const meta = getNotificationMeta(item.type);
  const { Icon } = meta;
  const hasRoute = hasNotificationNavigation(item);

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: "none",
        p: 0,
        mb: 1.5,
        bgcolor: "transparent",
        font: "inherit",
        fontFamily: FONT_FAMILY,
      }}
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          display: "flex",
          gap: 1.5,
          p: 2,
          borderRadius: RADIUS_CARD,
          bgcolor: "#fff",
          border: item.read ? `1px solid ${BORDER_DEFAULT}` : `1px solid ${BORDER_LIGHT}`,
          boxShadow: item.read ? "none" : "0 2px 8px rgba(117, 109, 233, 0.08)",
          transition: "box-shadow 0.15s ease",
          overflow: "hidden",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(117, 109, 233, 0.12)",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 3,
            background: item.read ? "transparent" : PRIMARY,
          },
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: RADIUS_CARD,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: meta.iconBg,
            ml: 0.5,
          }}
        >
          <Icon sx={{ fontSize: 24, color: meta.iconColor }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pr: hasRoute ? 0.5 : 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: meta.iconColor,
              }}
            >
              {meta.label}
            </Typography>
            {!item.read ? (
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: PRIMARY,
                }}
              />
            ) : null}
            <Typography sx={{ fontSize: 12, color: TEXT_MUTED, ml: "auto" }}>
              {formatTime(item.createdAt)}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontWeight: item.read ? 500 : 600,
              fontSize: 14,
              color: TEXT_PRIMARY,
              lineHeight: 1.35,
              mb: item.body ? 0.5 : 0,
            }}
          >
            {item.title}
          </Typography>

          {item.body ? (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 400,
                color: TEXT_SECONDARY,
                lineHeight: 1.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.body}
            </Typography>
          ) : null}
        </Box>

        {hasRoute ? (
          <Box sx={{ display: "flex", alignItems: "center", pr: 0.5, color: TEXT_MUTED }}>
            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default NotificationCard;
